import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Orders() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.order_type = typeFilter;
      const res = await axios.get(`${API}/orders`, { params, headers });
      setOrders(res.data.orders);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [page, search, statusFilter, typeFilter, token]);

  useEffect(() => { fetch(); }, [fetch]);

  const statusLabels = { pendiente: 'Pendiente', pagado: 'Pagado', cancelado: 'Cancelado', en_proceso: 'En Proceso', enviado: 'Enviado', entregado: 'Entregado', compra_interna: 'Compra Interna' };
  const statusColors = { pendiente: 'outline', pagado: 'secondary', cancelado: 'destructive', en_proceso: 'outline', enviado: 'secondary', entregado: 'secondary', compra_interna: 'outline' };
  const SHIPPING_META = {
    pending:         { label: 'Pendiente',     className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
    label_generated: { label: 'Guía Generada', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
    in_transit:      { label: 'En Tránsito',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
    delivered:       { label: 'Entregado',     className: 'bg-green-500/15 text-green-300 border-green-500/30' },
    failed:          { label: 'Fallido',       className: 'bg-red-500/15 text-red-300 border-red-500/30' },
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-semibold">Órdenes</h1>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por número, cliente..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="interna">Interna</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground self-center">{total} órdenes</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="px-3 py-2 text-left font-medium">Orden</th>
                  <th className="px-3 py-2 text-left font-medium">Cliente</th>
                  <th className="px-3 py-2 text-left font-medium">Tipo</th>
                  <th className="px-3 py-2 text-center font-medium">Estado</th>
                  <th className="px-3 py-2 text-center font-medium">Envío</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                  <th className="px-3 py-2 text-left font-medium">Fecha</th>
                  <th className="px-3 py-2 text-center font-medium">Ver</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Cargando...</td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin órdenes</td></tr>
                ) : orders.map(o => (
                  <tr key={o.id} className="border-b hover:bg-secondary/20 transition-colors duration-150">
                    <td className="px-3 py-2 font-mono text-xs font-medium">{o.order_number}</td>
                    <td className="px-3 py-2">
                      <p className="text-sm">{o.user_name}</p>
                      <p className="text-xs text-muted-foreground">{o.user_email}</p>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={o.order_type === 'interna' ? 'outline' : 'secondary'} className="text-xs">
                        {o.order_type === 'interna' ? 'Interna' : 'Normal'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant={statusColors[o.order_status] || 'outline'}>{statusLabels[o.order_status] || o.order_status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {o.envia_carrier && SHIPPING_META[o.shipping_status] ? (
                        <Badge variant="outline" className={`text-[10px] ${SHIPPING_META[o.shipping_status].className}`} data-testid={`shipping-badge-${o.order_number}`}>
                          {SHIPPING_META[o.shipping_status].label}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">${o.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString('es-MX')}</td>
                    <td className="px-3 py-2 text-center">
                      <Link to={`/admin/ordenes/${o.id}`}><Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <span className="flex items-center text-sm text-muted-foreground">Página {page} de {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </div>
      )}
    </div>
  );
}
